'use client';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import Modal from '@/components/ui/Modal';
import useSharedRef from '@/hooks/useSharedRef';
import { toast } from 'react-toastify';
import Link from 'next/link';

const ContactForm: React.FC = () => {
  const modalRef = useSharedRef('modalRef');
  const [open, setOpen] = useState<boolean>(false);
  const [defaultEmail, setDefaultEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [contactNo, setContactNo] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  useImperativeHandle(
    modalRef,
    () => ({
      open: (email: string) => {
        setDefaultEmail(email);
        setOpen(true);
      },
    }),
    [setOpen]
  );
  const submit = () => {
    const form = document.getElementById('contactForm') as HTMLFormElement;
    if (form.reportValidity()) {
      const payload = {
        name,
        email,
        phone: contactNo,
        message,
      };
      setOpen(false);
      fetch('https://contactus.sixsense.ai', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(() => {
          toast.clearWaitingQueue();
          toast.dismiss();
          toast.success(
            'Thank you! Your message has been sent. The Sixsense team will reach out soon.'
          );
        })
        .catch(() => {
          toast.clearWaitingQueue();
          toast.dismiss();
          toast.error('Something went wrong. Please try again later.');
        });
    }
    return;
  };

  return (
    <Modal open={open} className="flex items-stretch justify-center max-w-[1080px] ">
      <div className="hidden sm:block w-[33.75rem] h-[36.25rem] bg-cover bg-center">
        <div
          className="w-full h-full min-w-[540px] min-h-[580px] pt-[4.75rem] pb-[5.375rem] pl-[2rem] pr-[2rem] bg-cover flex justify-center align-center"
          style={{ backgroundImage: "url('/images/circuit_bg.svg')" }}
        >
          <div className="w-full h-full max-w-[475px] py-[2.25rem] px[1.5rem] flex flex-col gap-[1.875rem]">
            <div className="flex justify-start">
              <img src="/images/logo.svg" alt="sixsense logo" className="h-[3rem] w-auto" />
            </div>
            <div className="flex flex-col items-start gap-[1.5rem] ">
              <div className="self-stretch text-white font-ibm-plex text-[1.875rem] font-[500] leading-[140%]">
                Move a step closer to maximising yield & productivity
              </div>
              <div className="self-stretch text-white text-[1rem] font-ibm-plex font-[300] leading-[150%]">
                Contact us to explore how Sixsense can drive defect reduction, optimize performance,
                and unlock measurable ROI.
              </div>
              <div className="flex flex-col items-start gap-4">
                <div className="flex items-center gap-3">
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.59375 1.78125C0.25 1.875 0 2.15625 0 2.5C0 10.5312 6.5 17 14.5 17C14.875 17 15.1562 16.7812 15.25 16.4375L16 13.1875C16.0625 12.8438 15.9062 12.4688 15.5625 12.3125L12.0625 10.8125C11.75 10.6875 11.4062 10.7812 11.1875 11.0312L9.625 12.9375C7.1875 11.7812 5.21875 9.78125 4.09375 7.40625L6 5.84375C6.25 5.625 6.34375 5.28125 6.21875 4.96875L4.71875 1.46875C4.5625 1.125 4.1875 0.9375 3.84375 1.03125L0.59375 1.78125Z"
                      fill="white"
                    />
                  </svg>
                  <Link
                    href="tel:+65 94860203"
                    target="_blank"
                    className="text-white text-[1rem] font-ibm-plex font-[300] leading-[150%]"
                  >
                    +65 94860203
                  </Link>
                </div>
                <div className="flex items-center gap-3">
                  <svg
                    width="16"
                    height="12"
                    viewBox="0 0 16 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0.3125 3.96875C0.1875 3.875 0 3.96875 0 4.125V10.5C0 11.3438 0.6875 12 1.5 12H14.5C15.3438 12 16 11.3438 16 10.5V4.125C16 3.96875 15.8438 3.875 15.7188 3.96875C15 4.53125 14.0938 5.21875 10.9062 7.53125C10.25 8 9.125 9.03125 8 9.03125C6.90625 9.03125 5.75 8 5.125 7.53125C1.9375 5.21875 1.03125 4.53125 0.3125 3.96875ZM8 8C8.75 8.03125 9.78125 7.09375 10.3125 6.71875C14.4688 3.71875 14.7812 3.4375 15.7188 2.6875C15.9062 2.5625 16 2.34375 16 2.09375V1.5C16 0.6875 15.3438 0 14.5 0H1.5C0.6875 0 0 0.6875 0 1.5V2.09375C0 2.34375 0.125 2.5625 0.3125 2.6875C1.25 3.4375 1.5625 3.71875 5.71875 6.71875C6.25 7.09375 7.28125 8.03125 8 8Z"
                      fill="white"
                    />
                  </svg>
                  <Link
                    href="mailto:hello@sixsense.ai"
                    target="_blank"
                    className="text-white text-[1rem] font-ibm-plex font-[300] leading-[150%]"
                  >
                    hello@sixsense.ai
                  </Link>
                </div>
                <div className="flex items-center gap-3">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3.13375 14H0.23125V4.65292H3.13375V14ZM1.68094 3.37789C0.752813 3.37789 0 2.60912 0 1.68097C6.64312e-09 1.23515 0.177098 0.80759 0.492335 0.492346C0.807572 0.177102 1.23513 0 1.68094 0C2.12675 0 2.5543 0.177102 2.86954 0.492346C3.18478 0.80759 3.36188 1.23515 3.36188 1.68097C3.36188 2.60912 2.60875 3.37789 1.68094 3.37789ZM13.9969 14H11.1006V9.4499C11.1006 8.3655 11.0787 6.97484 9.59156 6.97484C8.0825 6.97484 7.85125 8.15299 7.85125 9.37177V14H4.95188V4.65292H7.73562V5.92794H7.77625C8.16375 5.19355 9.11031 4.41854 10.5225 4.41854C13.46 4.41854 14 6.35295 14 8.86551V14H13.9969Z"
                      fill="white"
                    />
                  </svg>
                  <Link
                    href="https://sg.linkedin.com/company/sixsense-pte-ltd"
                    target="_blank"
                    className="text-white text-[1rem] font-ibm-plex font-[300] leading-[150%]"
                  >
                    Sixsense
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-full sm:min-w-[540px] min-h-[580px]">
        <div className="flex justify-end p-[1.25rem]">
          <button onClick={() => setOpen(false)}>
            <svg
              width="14"
              height="13"
              viewBox="0 0 14 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.53906 6.5L13.3828 11.3438C13.5 11.4609 13.5 11.6562 13.3828 11.8125L12.5234 12.6719C12.3672 12.7891 12.1719 12.7891 12.0547 12.6719L11.2344 11.8516L7.25 7.82812L2.40625 12.6719C2.28906 12.7891 2.09375 12.7891 1.9375 12.6719L1.07812 11.8125C0.960938 11.6562 0.960938 11.4609 1.07812 11.3438L5.92188 6.5L1.07812 1.69531C0.960938 1.57812 0.960938 1.38281 1.07812 1.22656L1.9375 0.367188C2.09375 0.25 2.28906 0.25 2.40625 0.367188L7.25 5.21094L12.0547 0.367188C12.1719 0.25 12.3672 0.25 12.5234 0.367188L13.3828 1.22656C13.5 1.38281 13.5 1.57812 13.3828 1.69531L12.5625 2.51562L8.53906 6.5Z"
                fill="#656565"
              />
            </svg>
          </button>
        </div>
        <form className="flex px-[30px] w-full flex-col items-start gap-6" id="contactForm">
          <div className="flex w-full flex-col items-start gap-3">
            <span className="text-[#272727] text-[0.875rem] font-ibm-plex font-[400] leading-[140%]">
              Name
            </span>
            <input
              required
              minLength={4}
              onChange={(e) => setName(e.target.value)}
              className="h-[2.5rem] w-full rounded-md border border-[rgba(196, 217, 233, 0.70) bg-[#FBFCFE] px-3 py-2 text-[#272727] text-[0.875rem] font-ibm-plex font-[400] leading-[140%]"
            ></input>
          </div>
          <div className="flex w-full flex-col items-start gap-3">
            <span className="text-[#272727] text-[0.875rem] font-ibm-plex font-[400] leading-[140%]">
              Email
            </span>
            <input
              required
              type="email"
              defaultValue={defaultEmail}
              onChange={(e) => setEmail(e.target.value)}
              className="h-[2.5rem] w-full rounded-md border border-[rgba(196, 217, 233, 0.70) bg-[#FBFCFE] px-3 py-2 text-[#272727] text-[0.875rem] font-ibm-plex font-[400] leading-[140%]"
            ></input>
          </div>
          <div className="flex w-full flex-col items-start gap-3">
            <span className="text-[#272727] text-[0.875rem] font-ibm-plex font-[400] leading-[140%]">
              Contact no.
            </span>
            <input
              onChange={(e) => setContactNo(e.target.value)}
              className="h-[2.5rem] w-full rounded-md border border-[rgba(196, 217, 233, 0.70) bg-[#FBFCFE] px-3 py-2 text-[#272727] text-[0.875rem] font-ibm-plex  font-[400] leading-[140%]"
            ></input>
          </div>
          <div className="flex w-full flex-col items-start gap-3">
            <span className="text-[#272727] text-[0.875rem] font-ibm-plex font-[400] leading-[140%]">
              Message
            </span>
            <textarea
              rows={4}
              required
              minLength={10}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-md border border-[rgba(196, 217, 233, 0.70) bg-[#FBFCFE] px-3 py-2 text-[#272727] text-[0.875rem] font-ibm-plex font-[400] leading-[140%]"
            ></textarea>

            <button
              type="submit"
              onClick={submit}
              className="py-3 px-6 flex justify-center rounded-md bg-[#285CEB] text-white font-ibm-plex text-[1rem] leading-[175%]"
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ContactForm;
